import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBold,
  faEraser,
  faFont,
  faHighlighter,
  faItalic,
  faListOl,
  faListUl,
  faUnderline,
} from '@fortawesome/free-solid-svg-icons';
import { AppIcons } from '../../../../../core/icons/fa-icons';

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { TableKit } from '@tiptap/extension-table';
import { Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';

import {
  RICH_TEXT_COLORS,
  RICH_TEXT_FONT_FAMILIES,
  RICH_TEXT_FONT_SIZES,
  RICH_TEXT_HIGHLIGHTS,
  sanitizeRichText,
  toRichText,
} from '../../utils/rich-text.util';

type Palette = 'color' | 'highlight' | null;

/** What a toolbar table action deletes: the cursor's row, column or the whole table. */
type TablePart = 'row' | 'column' | 'table';

/**
 * Standard rich text editor for Credit Proposal sections: bold, italic,
 * underline, font family/size, text colour, highlight, bullet and numbered
 * lists, and tables (pasted from Word / Excel). Works with [(ngModel)] like a <textarea>.
 *
 * The model value is sanitized HTML (see rich-text.util), or '' when empty.
 * Legacy plain-text values are accepted and shown as paragraphs; they are only
 * converted to HTML once the user edits them.
 */
@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FontAwesomeModule],
  templateUrl: './rich-text-editor.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditor),
      multi: true,
    },
  ],
})
export class RichTextEditor implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() placeholder = '';
  /** Minimum height of the writing area, any CSS length. */
  @Input() minHeight = '160px';
  @Input() set editable(value: boolean) {
    this.isEditable = value !== false;
    // emitUpdate=false: toggling edit mode is not a content change.
    this.editor?.setEditable(this.isEditable && !this.isDisabled, false);
  }

  @ViewChild('editorHost', { static: true }) private editorHost!: ElementRef<HTMLDivElement>;
  @ViewChild('editorArea', { static: true }) private editorArea!: ElementRef<HTMLDivElement>;

  readonly fontFamilies = RICH_TEXT_FONT_FAMILIES;
  readonly fontSizes = RICH_TEXT_FONT_SIZES;
  readonly colors = RICH_TEXT_COLORS;
  readonly highlights = RICH_TEXT_HIGHLIGHTS;

  readonly icons = {
    bold: faBold,
    italic: faItalic,
    underline: faUnderline,
    color: faFont,
    highlight: faHighlighter,
    bulletList: faListUl,
    orderedList: faListOl,
    clear: faEraser,
    // The app's standard delete icon (AppIcons), as in the shared table's actions.
    delete: AppIcons.delete,
  };

  editor: Editor | null = null;
  openPalette: Palette = null;
  isFocused = false;
  isEditable = true;
  isDisabled = false;

  private pendingValue = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorHost.nativeElement,
      editable: this.isEditable && !this.isDisabled,
      content: toRichText(this.pendingValue),
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          code: false,
          codeBlock: false,
          horizontalRule: false,
          link: false,
          strike: false,
        }),
        TextStyle,
        Color,
        FontFamily,
        FontSize,
        Highlight.configure({ multicolor: true }),
        // Tables pasted from Word / Excel keep their rows and columns.
        TableKit.configure({ table: { resizable: false } }),
      ],
      editorProps: {
        attributes: {
          class: 'rich-text-content rich-text-editor__content',
          role: 'textbox',
          'aria-multiline': 'true',
        },
      },
      onUpdate: ({ editor }) => {
        const value = editor.isEmpty ? '' : sanitizeRichText(editor.getHTML());
        this.zone.run(() => this.onChange(value));
      },
      onSelectionUpdate: () => this.cdr.markForCheck(),
      onFocus: () => this.zone.run(() => (this.isFocused = true)),
      onBlur: () =>
        this.zone.run(() => {
          this.isFocused = false;
          this.onTouched();
        }),
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.editor = null;
  }

  /* ---------- ControlValueAccessor ---------- */

  writeValue(value: string | null | undefined): void {
    this.pendingValue = value ?? '';

    if (!this.editor) {
      return;
    }

    const next = toRichText(this.pendingValue);

    // Skip echoes of our own updates so the cursor position is preserved.
    const current = this.editor.isEmpty ? '' : sanitizeRichText(this.editor.getHTML());
    if (next !== current) {
      this.editor.commands.setContent(next, { emitUpdate: false });
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.editor?.setEditable(this.isEditable && !isDisabled, false);
  }

  /* ---------- Table actions (toolbar "Table" group) ---------- */

  readonly tableParts: ReadonlyArray<{ key: TablePart; label: string }> = [
    { key: 'row', label: 'RICH_TEXT.DELETE_ROW' },
    { key: 'column', label: 'RICH_TEXT.DELETE_COLUMN' },
    { key: 'table', label: 'RICH_TEXT.DELETE_TABLE' },
  ];

  /** True while the cursor is inside a table, which shows the toolbar's table group. */
  get isInTable(): boolean {
    return this.isActive('table');
  }

  /**
   * The area a hovered table action will delete (the cursor's row, column or
   * the whole table), tinted red so the user sees what goes before clicking.
   * Px relative to the writing area; an overlay, since ProseMirror owns the table's DOM.
   */
  deletePreview: { top: number; left: number; width: number; height: number } | null = null;

  showDeletePreview(part: TablePart): void {
    const cell = this.currentCell();
    const table = cell?.closest('table');

    if (!cell || !table) {
      this.deletePreview = null;
      return;
    }

    const area = this.editorArea.nativeElement.getBoundingClientRect();
    const tableBox = table.getBoundingClientRect();
    const cellBox = cell.getBoundingClientRect();
    const rowBox = (cell.parentElement as HTMLElement).getBoundingClientRect();

    // A row spans the table's width, a column its height.
    const box =
      part === 'row'
        ? { top: rowBox.top, left: tableBox.left, width: tableBox.width, height: rowBox.height }
        : part === 'column'
          ? { top: tableBox.top, left: cellBox.left, width: cellBox.width, height: tableBox.height }
          : tableBox;

    this.deletePreview = {
      top: box.top - area.top,
      left: box.left - area.left,
      width: box.width,
      height: box.height,
    };
  }

  hideDeletePreview(): void {
    this.deletePreview = null;
  }

  /**
   * Deletes the cursor's row or column, or the whole table. Deleting the last
   * row or column removes the table, since prosemirror-tables won't leave it empty.
   */
  deleteTablePart(part: TablePart): void {
    const editor = this.editor;

    if (!editor) {
      return;
    }

    this.deletePreview = null;

    const deleted =
      part === 'row'
        ? editor.chain().focus().deleteRow().run()
        : part === 'column'
          ? editor.chain().focus().deleteColumn().run()
          : false;

    if (!deleted) {
      editor.chain().focus().deleteTable().run();
    }
  }

  /** The table cell holding the cursor, or null outside a table. */
  private currentCell(): HTMLTableCellElement | null {
    const editor = this.editor;

    if (!editor) {
      return null;
    }

    const { node } = editor.view.domAtPos(editor.state.selection.from);
    const element = node instanceof HTMLElement ? node : node.parentElement;

    return element?.closest('td, th') ?? null;
  }

  /* ---------- Toolbar ---------- */

  get canEdit(): boolean {
    return this.isEditable && !this.isDisabled;
  }

  get isEmpty(): boolean {
    return this.editor?.isEmpty ?? !this.pendingValue;
  }

  isActive(name: string, attributes?: Record<string, unknown>): boolean {
    return this.editor?.isActive(name, attributes) ?? false;
  }

  get activeFontFamily(): string {
    return (this.editor?.getAttributes('textStyle')['fontFamily'] as string) ?? '';
  }

  get activeFontSize(): string {
    return (this.editor?.getAttributes('textStyle')['fontSize'] as string) ?? '';
  }

  toggleBold(): void {
    this.editor?.chain().focus().toggleBold().run();
  }

  toggleItalic(): void {
    this.editor?.chain().focus().toggleItalic().run();
  }

  toggleUnderline(): void {
    this.editor?.chain().focus().toggleUnderline().run();
  }

  toggleBulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run();
  }

  setFontFamily(fontFamily: string): void {
    const chain = this.editor?.chain().focus();
    (fontFamily ? chain?.setFontFamily(fontFamily) : chain?.unsetFontFamily())?.run();
  }

  setFontSize(fontSize: string): void {
    const chain = this.editor?.chain().focus();
    (fontSize ? chain?.setFontSize(fontSize) : chain?.unsetFontSize())?.run();
  }

  setColor(color: string | null): void {
    const chain = this.editor?.chain().focus();
    (color ? chain?.setColor(color) : chain?.unsetColor())?.run();
    this.openPalette = null;
  }

  setHighlight(color: string | null): void {
    const chain = this.editor?.chain().focus();
    (color ? chain?.setHighlight({ color }) : chain?.unsetHighlight())?.run();
    this.openPalette = null;
  }

  clearFormatting(): void {
    this.editor?.chain().focus().unsetAllMarks().clearNodes().run();
  }

  togglePalette(palette: Exclude<Palette, null>): void {
    this.openPalette = this.openPalette === palette ? null : palette;
  }

  focusEditor(): void {
    if (this.canEdit) {
      this.editor?.commands.focus();
    }
  }

  /** The component's host element (lets parents match an instance to a DOM id). */
  get hostElement(): HTMLElement {
    return this.host.nativeElement;
  }

  /**
   * Inserts plain text at the current (or last) cursor position, padded with a
   * space where it would otherwise run into adjacent text. Returns false when
   * the editor is not ready, so callers can fall back to editing the model.
   */
  insertText(text: string): boolean {
    if (!this.editor || !this.canEdit) {
      return false;
    }

    const { from, to } = this.editor.state.selection;
    const doc = this.editor.state.doc;
    const before = from > 1 ? doc.textBetween(from - 1, from) : '';
    const after = to < doc.content.size - 1 ? doc.textBetween(to, to + 1) : '';

    const padded = (before && !/\s/.test(before) ? ' ' : '') + text + (after && !/\s/.test(after) ? ' ' : '');

    this.editor.chain().focus().insertContentAt({ from, to }, padded).run();
    return true;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.openPalette && !this.host.nativeElement.contains(event.target as Node)) {
      this.openPalette = null;
    }
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.openPalette = null;
  }
}
